import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ClipboardList, GitBranch, Users, Clock, Calendar, AlertTriangle, TrendingUp, Stethoscope } from "lucide-react";
import { API } from "@/config/api";
import { REP_DIRECTORY, getRepDisplayName } from "@/config/constants";

const toISO = (d) => d.toISOString().split("T")[0];
const today = () => new Date(new Date().toDateString());

const formatDateLong = (dateStr) => {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

const getWorkingDays = (year, month) => {
  let count = 0;
  const d = new Date(year, month, 1);
  while (d.getMonth() === month) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
};

const getWorkingDaysElapsed = (year, month, upToDay) => {
  let count = 0;
  const d = new Date(year, month, 1);
  while (d.getMonth() === month && d.getDate() <= upToDay) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
};

const toSAST = (isoStr) => {
  try {
    const d = new Date(isoStr);
    return d.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Africa/Johannesburg" });
  } catch {
    return "-";
  }
};

const Skeleton = ({ className }) => <div className={`animate-pulse bg-muted rounded ${className}`} />;

const EodPage = () => {
  const [user, setUser] = useState(null);
  const [selectedDate, setSelectedDate] = useState(toISO(today()));
  const [visits, setVisits] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [monthReferrals, setMonthReferrals] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [monthVisits, setMonthVisits] = useState([]);
  const [monthAppts, setMonthAppts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(r => setUser(r.data)).catch(() => {});
  }, []);

  const fetchData = useCallback(async (date) => {
    setLoading(true);
    const safeGet = (url) => axios.get(url, { withCredentials: true }).then(r => r.data).catch(() => null);

    const d = new Date(date + "T00:00:00");
    const monthStart = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;

    const [visitsD, referralsD, apptsD, monthRefD, monthVisD, monthApptsD] = await Promise.all([
      safeGet(`${API}/visits?start_date=${date}&end_date=${date}`),
      safeGet(`${API}/crm/cases?start_date=${date}&end_date=${date}`),
      safeGet(`${API}/calendar/appointments?date_from=${date}&date_to=${date}`),
      safeGet(`${API}/crm/cases?start_date=${monthStart}&end_date=${date}`),
      safeGet(`${API}/visits?start_date=${monthStart}&end_date=${date}`),
      safeGet(`${API}/calendar/appointments?date_from=${monthStart}&date_to=${date}`),
    ]);

    setVisits(Array.isArray(visitsD) ? visitsD : visitsD?.visits || []);
    setReferrals(Array.isArray(referralsD) ? referralsD : referralsD?.cases || []);
    setAppointments(Array.isArray(apptsD) ? apptsD : apptsD?.appointments || []);
    setMonthReferrals(Array.isArray(monthRefD) ? monthRefD : monthRefD?.cases || []);
    setMonthVisits(Array.isArray(monthVisD) ? monthVisD : monthVisD?.visits || []);
    setMonthAppts(Array.isArray(monthApptsD) ? monthApptsD : monthApptsD?.appointments || []);
    setLoading(false);
  }, []);

  useEffect(() => { if (user) fetchData(selectedDate); }, [selectedDate, user, fetchData]);

  if (user && user.role === "rep") {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
        <h2 className="text-lg font-semibold" style={{ fontFamily: "Manrope, sans-serif" }}>Access Denied</h2>
        <p className="text-sm text-muted-foreground">This page is only available to managers and admins.</p>
      </div>
    );
  }

  const d = new Date(selectedDate + "T00:00:00");
  const totalWorkingDays = getWorkingDays(d.getFullYear(), d.getMonth());
  const elapsedWorkingDays = getWorkingDaysElapsed(d.getFullYear(), d.getMonth(), d.getDate());
  const monthRefCount = monthReferrals.length;
  const projected = elapsedWorkingDays > 0 ? Math.round((monthRefCount / elapsedWorkingDays) * totalWorkingDays) : 0;

  // Section 5: Rep Activity Times
  const repTimes = REP_DIRECTORY.map(rep => {
    const repVisits = visits.filter(v => v.rep_name === rep);
    const repAppts = appointments.filter(a => a.rep_name === rep);
    const allTimestamps = [
      ...repVisits.map(v => v.created_at).filter(Boolean),
      ...repAppts.map(a => a.created_at).filter(Boolean),
    ].sort();
    const total = repVisits.length + repAppts.length;
    return {
      rep,
      first: allTimestamps.length > 0 ? toSAST(allTimestamps[0]) : null,
      last: allTimestamps.length > 1 ? toSAST(allTimestamps[allTimestamps.length - 1]) : allTimestamps.length === 1 ? toSAST(allTimestamps[0]) : null,
      total,
    };
  });

  // Section 4: Month visits & appts per rep
  const repMonthData = REP_DIRECTORY.map(rep => ({
    rep,
    visits: monthVisits.filter(v => v.rep_name === rep).length,
    appts: monthAppts.filter(a => a.rep_name === rep).length,
    total: monthVisits.filter(v => v.rep_name === rep).length + monthAppts.filter(a => a.rep_name === rep).length,
  }));

  // New doctors (created in last 7 days among today's referrals)
  const sevenDaysAgo = new Date(d); sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const newDoctorReferrals = referrals.filter(r => {
    if (!r.created_at) return false;
    return new Date(r.created_at) >= sevenDaysAgo;
  });

  return (
    <div className="p-4 lg:p-6 space-y-5 pb-6" data-testid="eod-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>
            End of Day Report
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {formatDateLong(selectedDate)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Date:</Label>
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="h-9 w-40 text-sm"
            data-testid="eod-date-picker"
          />
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-40" />)}
        </div>
      ) : (
        <>
          {/* 1. Total Referrals Today */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
                <GitBranch className="w-4 h-4 text-purple-500" />
                Referrals Today
                <Badge variant="secondary" className="ml-auto">{referrals.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {referrals.length > 0 ? (
                <div className="space-y-2">
                  {referrals.map((r, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg text-xs">
                      <div className="flex items-center gap-2">
                        <Stethoscope className="w-3.5 h-3.5 text-purple-500" />
                        <span className="font-medium">{r.doctor_name || r.referring_doctor || "-"}</span>
                        <Badge variant="outline" className="text-[10px]">{getRepDisplayName(r.assigned_rep_name || r.rep_name)}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.medical_aid && <Badge variant="secondary" className="text-[10px]">{r.medical_aid}</Badge>}
                        {r.patient_name && <span className="text-muted-foreground">{r.patient_name} {r.patient_surname || ""}</span>}
                      </div>
                    </div>
                  ))}
                  {newDoctorReferrals.length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <p className="text-xs font-semibold text-blue-700 mb-1">New Doctors Referred Today ({newDoctorReferrals.length})</p>
                      <p className="text-[11px] text-blue-600">Doctors added to the system in the last 7 days</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No referrals logged on this date</p>
              )}
            </CardContent>
          </Card>

          {/* 2 + 3. Month Referrals + Projection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-5">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Month Referrals to Date</p>
                <p className="text-3xl font-bold mt-2" style={{ fontFamily: "Manrope, sans-serif" }}>{monthRefCount}</p>
                <p className="text-xs text-muted-foreground mt-1">{elapsedWorkingDays} working days elapsed</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50/50 border-blue-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                  <p className="text-xs text-blue-700 uppercase tracking-wide">Projected Month Total</p>
                </div>
                <p className="text-3xl font-bold mt-2 text-blue-700" style={{ fontFamily: "Manrope, sans-serif" }}>{projected}</p>
                <p className="text-xs text-blue-600 mt-1">
                  Based on {elapsedWorkingDays} of {totalWorkingDays} working days
                </p>
              </CardContent>
            </Card>
          </div>

          {/* 4. Month Visits & Appointments Per Rep */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
                <Users className="w-4 h-4 text-blue-500" />
                Month Visits & Appointments Per Rep
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Rep</TableHead>
                    <TableHead className="text-xs text-center">Visits This Month</TableHead>
                    <TableHead className="text-xs text-center">Appointments This Month</TableHead>
                    <TableHead className="text-xs text-center">Total Entries</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {repMonthData.map(r => (
                    <TableRow key={r.rep}>
                      <TableCell className="text-xs font-medium">{getRepDisplayName(r.rep)}</TableCell>
                      <TableCell className="text-xs text-center">{r.visits}</TableCell>
                      <TableCell className="text-xs text-center">{r.appts}</TableCell>
                      <TableCell className="text-xs text-center">
                        <Badge variant="secondary">{r.total}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* 5. Rep Activity Times */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
                <Clock className="w-4 h-4 text-emerald-500" />
                Rep Activity Times
              </CardTitle>
              <CardDescription className="text-xs">First and last entry on {formatDateLong(selectedDate)}</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Rep</TableHead>
                    <TableHead className="text-xs text-center">First Entry</TableHead>
                    <TableHead className="text-xs text-center">Last Entry</TableHead>
                    <TableHead className="text-xs text-center">Total Entries</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {repTimes.map(r => (
                    <TableRow key={r.rep} className={r.total === 0 ? "opacity-50" : ""}>
                      <TableCell className="text-xs font-medium">{getRepDisplayName(r.rep)}</TableCell>
                      <TableCell className="text-xs text-center">{r.first || <span className="text-muted-foreground">No activity</span>}</TableCell>
                      <TableCell className="text-xs text-center">{r.last || <span className="text-muted-foreground">-</span>}</TableCell>
                      <TableCell className="text-xs text-center">
                        <Badge variant={r.total > 0 ? "secondary" : "outline"}>{r.total}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default EodPage;
